import { useState, useMemo } from "react"
import { createPortal } from "react-dom"
import {
  X,
  Plus,
  Trash2,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Loader2,
  Save,
} from "lucide-react"
import type { ChecklistData } from "../lib/checklistTemplate"
import { useModalOpen } from "../lib/useModalOpen"
import { GlitchWord } from "./GlitchWord"
import { GlitchSelect } from "./GlitchSelect"

export interface TemplateEditorModalProps {
  /** null = create new template */
  initialName?: string
  initialServiceType?: string
  initialChecklist?: ChecklistData
  /** Built-in templates can't be directly overwritten — but can be "saved as" a custom copy */
  isBuiltIn?: boolean
  availableServiceTypes: string[]
  onManageActivityTypes?: () => void
  onAddActivityType?: (name: string) => Promise<void>
  onUse: (name: string, serviceType: string, checklist: ChecklistData) => void
  /** Save or save-as handler. For built-in templates, the name will be user-provided (copy). */
  onSave?: (name: string, serviceType: string, checklist: ChecklistData) => Promise<void>
  onDelete?: () => Promise<void>
  onClose: () => void
}

export function TemplateEditorModal({
  initialName = "",
  initialServiceType = "",
  initialChecklist,
  isBuiltIn = false,
  availableServiceTypes,
  onManageActivityTypes,
  onAddActivityType,
  onUse,
  onSave,
  onDelete,
  onClose,
}: TemplateEditorModalProps) {
  useModalOpen(true)

  const [name, setName] = useState(initialName)
  const [saveAsName, setSaveAsName] = useState("")
  const [showSaveAs, setShowSaveAs] = useState(false)
  const [serviceType, setServiceType] = useState(initialServiceType)
  const [checklist, setChecklist] = useState<ChecklistData>(
    () => initialChecklist ? JSON.parse(JSON.stringify(initialChecklist)) : {}
  )
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [addingType, setAddingType] = useState(false)
  const [newTypeDraft, setNewTypeDraft] = useState("")
  const [savingType, setSavingType] = useState(false)

  async function handleAddType() {
    const trimmed = newTypeDraft.trim()
    if (!trimmed || !onAddActivityType || availableServiceTypes.includes(trimmed)) return
    setSavingType(true)
    try {
      await onAddActivityType(trimmed)
      setServiceType(trimmed)
    } finally {
      setNewTypeDraft("")
      setAddingType(false)
      setSavingType(false)
    }
  }

  const isNew = !initialName
  const categoryKeys = useMemo(() => Object.keys(checklist), [checklist])
  const totalTasks = categoryKeys.reduce((n, c) => n + (checklist[c]?.tasks?.length ?? 0), 0)

  const defaultOpen = useMemo(() => {
    const o: Record<string, boolean> = {}
    categoryKeys.forEach((cat, i) => { o[cat] = i === 0 })
    return o
  }, [categoryKeys])
  const isCategoryOpen = (cat: string) => openCategories[cat] ?? defaultOpen[cat] ?? false

  const allExpanded = categoryKeys.length > 0 && categoryKeys.every((c) => isCategoryOpen(c))
  function toggleAllExpanded() {
    const next = !allExpanded
    setOpenCategories(categoryKeys.reduce((acc, c) => ({ ...acc, [c]: next }), {}))
  }

  async function handleSave() {
    if (!onSave) return
    const saveName = isBuiltIn ? saveAsName.trim() : name.trim()
    if (!saveName) return
    setSaving(true)
    try {
      await onSave(saveName, serviceType, checklist)
      setShowSaveAs(false)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete()
    } finally {
      setDeleting(false)
    }
  }

  function handleUse() {
    onUse(name.trim() || "Untitled", serviceType, checklist)
  }

  return createPortal(
    <div className="fixed inset-0 z-[115] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/28 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-2xl max-h-[88vh] flex flex-col bg-black/42 backdrop-blur-xl rounded-2xl shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] border border-white/[0.08] overflow-hidden">
        {/* Header */}
        <div className="shrink-0 px-4 py-3 border-b border-white/[0.06]">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <h3 className="text-[11px] font-mono font-bold text-white/90 uppercase tracking-widest">
                {isNew ? "Create template" : isBuiltIn ? "Customize template" : "Edit template"}
              </h3>
              <div className="font-mono text-[9px] flex items-center gap-1.5">
                <span className="text-white/15">0x04</span>
                <span className="text-white/10">&middot;</span>
                <span className="text-teal-400/60">
                  <GlitchWord word="template" intensity={1} colorBase="teal" />.{isNew ? "create" : "edit"}()
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {categoryKeys.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllExpanded}
                  className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                  title={allExpanded ? "Collapse all" : "Expand all"}
                >
                  <ChevronsUpDown className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {/* Template name & service type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5">
                Template name {!isBuiltIn && "*"}
              </label>
              {isBuiltIn ? (
                <div className="px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02] font-mono text-[11px] text-white/50">
                  {name}
                </div>
              ) : (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/20"
                  placeholder="My template…"
                  autoFocus={isNew}
                />
              )}
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest">
                  Activity type
                </label>
                {onManageActivityTypes && (
                  <button
                    type="button"
                    onClick={onManageActivityTypes}
                    className="text-[9px] font-mono font-bold uppercase tracking-wider text-teal-300/70 hover:text-teal-200 transition-colors"
                  >
                    Edit types
                  </button>
                )}
              </div>
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <GlitchSelect
                      value={serviceType || null}
                      onChange={(v) => setServiceType(v)}
                      options={availableServiceTypes.map((t) => ({ value: t, label: t }))}
                      placeholder="Select…"
                      aria-label="Activity type"
                    />
                  </div>
                  {onAddActivityType && (
                    <button
                      type="button"
                      onClick={() => { setAddingType(v => !v); setNewTypeDraft("") }}
                      className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/40 hover:text-white/80 hover:border-teal-400/30 transition-colors"
                      aria-label="Add activity type"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {addingType && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border border-white/[0.08] bg-black/90 backdrop-blur-xl p-3 space-y-2 shadow-xl">
                    <input
                      autoFocus
                      value={newTypeDraft}
                      onChange={e => setNewTypeDraft(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") handleAddType(); if (e.key === "Escape") setAddingType(false) }}
                      placeholder="New type name…"
                      className="w-full bg-transparent font-mono text-[12px] text-white/80 border-b border-white/[0.08] pb-1 outline-none placeholder:text-white/20"
                    />
                    <div className="flex items-center gap-2 justify-end">
                      <button type="button" onClick={() => setAddingType(false)} className="font-mono text-[10px] text-white/30 hover:text-white/60 transition-colors">Cancel</button>
                      <button type="button" onClick={handleAddType} disabled={savingType || !newTypeDraft.trim()} className="font-mono text-[10px] text-teal-400 hover:text-teal-300 disabled:opacity-40 transition-colors">
                        {savingType ? "Saving\u2026" : "Add"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Checklist editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest">
                Checklist {totalTasks > 0 && <span className="text-white/15 ml-1">({totalTasks} tasks)</span>}
              </span>
            </div>
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
              <div className="p-3 border-b border-white/[0.06]">
                <button
                  type="button"
                  onClick={() => {
                    const catName = "New category"
                    setChecklist((prev) => ({ ...prev, [catName]: { tasks: [] } }))
                    setOpenCategories((p) => ({ ...p, [catName]: true }))
                  }}
                  className="flex items-center gap-2 text-[11px] font-mono text-white/40 hover:text-white/70 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add category
                </button>
              </div>
              {categoryKeys.length === 0 && (
                <div className="px-4 py-6 text-center">
                  <p className="font-mono text-[11px] text-white/20">No categories yet. Add one above.</p>
                </div>
              )}
              {categoryKeys.map((category, catIdx) => {
                const data = checklist[category]
                const tasks = data?.tasks ?? []
                const isOpen = isCategoryOpen(category)
                const catChecked = tasks.filter((t) => t.completed).length
                const allCatChecked = tasks.length > 0 && catChecked === tasks.length
                return (
                  <div key={catIdx} className="border-b border-white/[0.06] last:border-b-0">
                    <div className="w-full flex items-center gap-2 px-4 py-3 hover:bg-white/[0.04] transition-colors">
                      <button
                        type="button"
                        onClick={() => setOpenCategories((p) => ({ ...p, [category]: !isOpen }))}
                        className="p-0.5 rounded shrink-0 text-white/40 hover:text-white/70"
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => {
                          const next = e.target.value
                          if (!next.trim()) return
                          setChecklist((prev) => {
                            const keys = Object.keys(prev)
                            const nextCl: ChecklistData = {}
                            keys.forEach((k) => { nextCl[k === category ? next : k] = prev[k] })
                            return nextCl
                          })
                          setOpenCategories((p) => {
                            const { [category]: val, ...rest } = p
                            return { ...rest, [next]: val }
                          })
                        }}
                        className="flex-1 min-w-0 text-[11px] font-mono font-semibold bg-transparent border-0 border-b border-transparent hover:border-white/20 focus:border-teal-400/50 focus:outline-none focus:ring-0 px-0 py-0.5 text-white/90"
                      />
                      {tasks.length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setChecklist((prev) => ({
                              ...prev,
                              [category]: { tasks: prev[category].tasks.map((t) => ({ ...t, completed: !allCatChecked })) },
                            }))
                          }
                          className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            allCatChecked
                              ? "bg-teal-500/30 border-teal-400/40 text-teal-400"
                              : "border-white/15 text-transparent hover:border-white/30 hover:text-white/30"
                          }`}
                          title={allCatChecked ? "Uncheck category" : "Check category"}
                        >
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </button>
                      )}
                      <span className="text-[9px] font-mono text-white/20 tabular-nums shrink-0">
                        {tasks.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setChecklist((prev) => { const next = { ...prev }; delete next[category]; return next })
                          setOpenCategories((p) => { const { [category]: _, ...rest } = p; return rest })
                        }}
                        className="p-1 rounded text-white/15 hover:text-red-400 hover:bg-red-500/10 shrink-0 transition-colors"
                        aria-label="Remove category"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-3 pt-0 pl-10 space-y-1.5">
                        {tasks.map((task, idx) => (
                          <div key={idx} className="flex items-center gap-2 group">
                            <button
                              type="button"
                              onClick={() =>
                                setChecklist((prev) => {
                                  const cat = { ...prev[category], tasks: [...prev[category].tasks] }
                                  cat.tasks[idx] = { ...cat.tasks[idx], completed: !cat.tasks[idx].completed }
                                  return { ...prev, [category]: cat }
                                })
                              }
                              className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                task.completed
                                  ? "bg-emerald-500/80 border-emerald-400 text-white"
                                  : "border-white/20 hover:border-white/40"
                              }`}
                            >
                              {task.completed && <Check className="w-3 h-3" strokeWidth={3} />}
                            </button>
                            <input
                              type="text"
                              value={task.task}
                              onChange={(e) =>
                                setChecklist((prev) => {
                                  const cat = { ...prev[category], tasks: [...prev[category].tasks] }
                                  cat.tasks[idx] = { ...cat.tasks[idx], task: e.target.value }
                                  return { ...prev, [category]: cat }
                                })
                              }
                              className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-[11px] font-mono text-white/80 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-teal-400/30"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setChecklist((prev) => {
                                  const cat = { ...prev[category], tasks: prev[category].tasks.filter((_, i) => i !== idx) }
                                  return { ...prev, [category]: cat }
                                })
                              }
                              className="p-1.5 rounded text-red-400/80 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                              aria-label="Remove task"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            setChecklist((prev) => ({
                              ...prev,
                              [category]: { ...prev[category], tasks: [...prev[category].tasks, { task: "New task", completed: false }] },
                            }))
                          }
                          className="flex items-center gap-1.5 text-[11px] font-mono text-white/40 hover:text-white/70 py-1 transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" /> Add task
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-4 py-3 border-t border-white/[0.06] space-y-2">
          {/* Save-as row for built-in templates */}
          {isBuiltIn && onSave && showSaveAs && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-teal-400/20 bg-teal-500/[0.06] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/25"
                placeholder="Custom template name…"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && saveAsName.trim()) handleSave() }}
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !saveAsName.trim()}
                className="flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-amber-400/25 bg-amber-500/12 text-amber-300 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-amber-500/20 transition-colors disabled:opacity-50 shrink-0"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => { setShowSaveAs(false); setSaveAsName("") }}
                className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2">
            {/* Delete — custom templates only */}
            {onDelete && !isBuiltIn && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-1.5 py-2 px-3 rounded-lg border border-red-400/20 bg-red-500/10 text-red-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-red-500/20 transition-colors disabled:opacity-50 mr-auto"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete
              </button>
            )}
            <div className={onDelete && !isBuiltIn ? "" : "ml-auto"} />

            {/* Save — custom templates: direct save. Built-in: toggle save-as row. */}
            {onSave && (
              isBuiltIn ? (
                !showSaveAs && (
                  <button
                    type="button"
                    onClick={() => setShowSaveAs(true)}
                    className="flex items-center gap-1.5 py-2 px-3 rounded-lg border border-amber-400/25 bg-amber-500/12 text-amber-300 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-amber-500/20 transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Save as custom
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !name.trim()}
                  className="flex items-center gap-1.5 py-2 px-3 rounded-lg border border-amber-400/25 bg-amber-500/12 text-amber-300 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {isNew ? "Save template" : "Save"}
                </button>
              )
            )}

            <button
              type="button"
              onClick={handleUse}
              className="flex items-center gap-1.5 py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/15 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/25 transition-colors"
            >
              Use template
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
